import clsx from "clsx";
import { useState } from "react";
import { basisLabel } from "../../shared/money";
import { plural } from "../../shared/words";
import { ErrorNotice, Notice } from "../../web/components/ui";
import { WindowPopup } from "../../web/components/WindowPopup";
import { useMoney } from "../../web/lib/money";
import { can, itemName, itemTax, usePriceListSaver } from "./data";
import type { MaterialRow, PriceListData } from "./meta";

/*
 * Merging two items that are one (merge.ts): an item and a duplicate of it (setup.ts duplicatesOf: the same item number or name),
 * side by side, which to keep (one linked to the Catalogue, else the one known here, else the one in more bundles, to start with:
 * a linked one can't be the one archived, merge.ts), and whether it takes the other's
 * cost (to start with, when the other is new from ServiceM8: an import's, the supplier's new cost). The other's bundles and supplier
 * prices move to the one kept, and it's archived.
 */

export function MergePopup(
    props: Readonly<{ data: PriceListData; item: MaterialRow; duplicates: readonly MaterialRow[]; onClose: () => void; onMerged: (keptUuid: string) => void }>,
) {
    const { data, item, duplicates } = props;
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const unknown = new Set(data.unknownItems);
    const bundlesOf = (m: MaterialRow) => data.bundles.filter((b) => b.active && b.material_list.some((l) => l.uuid === m.uuid)).length;
    const [otherUuid, setOtherUuid] = useState(duplicates[0]?.uuid ?? "");
    const other = duplicates.find((d) => d.uuid === otherUuid) ?? duplicates[0];
    // Linked to the Catalogue (packages.ts): never the one archived (its links would go with it).
    const linked = (m: MaterialRow) => m.uuid in data.packages;
    // To start with: keep the one linked, else the one known here (the other new from ServiceM8), else the one in more bundles, else this one.
    const startKeep = (o: MaterialRow) => {
        if (linked(item) !== linked(o)) return linked(item) ? item.uuid : o.uuid;
        if (unknown.has(item.uuid) !== unknown.has(o.uuid)) return unknown.has(item.uuid) ? o.uuid : item.uuid;
        return bundlesOf(o) > bundlesOf(item) ? o.uuid : item.uuid;
    };
    const [keepUuid, setKeepUuid] = useState(other ? startKeep(other) : item.uuid);
    // Its cost taken, to start with, when the one merged is new from ServiceM8 (an import's: the supplier's new cost). Picked again
    // with each change of which is kept (or of the duplicate), so it's never left on from another choice.
    const takesCostFrom = (removed: string) => unknown.has(removed);
    const [takeCost, setTakeCost] = useState(other ? takesCostFrom(keepUuid === item.uuid ? other.uuid : item.uuid) : false);
    const choose = (keepIt: string, otherIt: MaterialRow) => {
        setKeepUuid(keepIt);
        setTakeCost(takesCostFrom(keepIt === item.uuid ? otherIt.uuid : item.uuid));
    };
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    if (!other) return null;
    const keep = keepUuid === item.uuid ? item : other;
    const remove = keep === item ? other : item;
    // Both linked: neither can go until one is unlinked.
    const bothLinked = linked(item) && linked(other);
    // Its cost can come from the other only while it's typed in (not following a supplier price, a labour rate or its products).
    const costTakeable = !data.costFollows.includes(keep.uuid) && !data.itemPricing[keep.uuid]?.labour && !(keep.uuid in data.packages);
    const shownCost = (m: MaterialRow) => `${fmt.money(m.cost)} ${basisLabel(m.price_includes_taxes, itemTax(data, m).rate)}`;
    const merge = () => {
        setBusy(true);
        setError(null);
        saver
            .mergeItems(keep.uuid, remove.uuid, takeCost && costTakeable)
            .then(() => props.onMerged(keep.uuid))
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };
    const card = (m: MaterialRow) => {
        const kept = m.uuid === keep.uuid;
        const prices = data.supplierPrices.filter((p) => p.materialUuid === m.uuid).length;
        // Keeping it would archive the other: not when that's linked (and this isn't), so it can't be picked.
        const otherOne = m === item ? other : item;
        const fixed = !bothLinked && linked(otherOne);
        return (
            <label key={m.uuid} className={clsx("block space-y-1 rounded-md border p-3", kept ? "border-brand-500 bg-brand-500/5" : "border-line", fixed ? "cursor-not-allowed" : "cursor-pointer")}>
                <span className="flex items-center gap-2">
                    <input type="radio" name="keep" className="accent-brand-600" checked={kept} disabled={fixed || bothLinked} onChange={() => choose(m.uuid, other)} />
                    <span className="text-xs font-semibold">{kept ? "Keep" : "Merge into the other"}</span>
                </span>
                <span className="block truncate font-medium" title={itemName(data, m)}>
                    {itemName(data, m)}
                </span>
                <span className="block text-xs text-muted">
                    {m.item_number || "No item number"} · {m.hidden ? "Hidden" : "For sale"}
                    {unknown.has(m.uuid) ? " · New from ServiceM8" : ""}
                </span>
                <span className="block text-xs text-muted tabular-nums">
                    {fmt.money(m.price)} · cost {shownCost(m)}
                </span>
                <span className="block text-xs text-muted">
                    {plural(bundlesOf(m), "bundle")} · {plural(prices, "supplier price")}
                </span>
                {linked(m) ? <span className="block text-xs text-brand-ink">{kept && !bothLinked ? "Linked to the Catalogue, so it's the one kept" : "Linked to the Catalogue"}</span> : null}
            </label>
        );
    };
    return (
        <WindowPopup title="Merge duplicates" onClose={props.onClose}>
            <div className="space-y-3 text-sm">
                {duplicates.length > 1 ? (
                    <label className="block space-y-1">
                        <span className="field-label block">Its duplicate</span>
                        <select
                            className="input"
                            value={other.uuid}
                            onChange={(e) => {
                                const next = duplicates.find((d) => d.uuid === e.target.value);
                                setOtherUuid(e.target.value);
                                if (next) choose(startKeep(next), next);
                            }}
                        >
                            {duplicates.map((d) => (
                                <option key={d.uuid} value={d.uuid}>
                                    {itemName(data, d)} ({d.item_number || "no item number"})
                                </option>
                            ))}
                        </select>
                    </label>
                ) : null}
                <div className="grid gap-2 sm:grid-cols-2">
                    {card(item)}
                    {card(other)}
                </div>
                {can(data, "costs.edit") ? (
                    <label className={clsx("flex items-start gap-2", !costTakeable && "opacity-60")}>
                        <input type="checkbox" className="mt-0.5 size-4 accent-brand-600" disabled={!costTakeable} checked={takeCost && costTakeable} onChange={(e) => setTakeCost(e.target.checked)} />
                        <span>
                            Use its cost ({shownCost(remove)})
                            <span className="block text-xs text-muted">
                                {costTakeable ? "e.g. the supplier's new price, from an import." : "The one kept takes its cost from a supplier price, a labour rate or its products."}
                            </span>
                        </span>
                    </label>
                ) : null}
                <p className="m-0 text-xs text-muted">
                    The other&apos;s bundles and supplier prices move to the one kept, and it&apos;s archived. Past jobs keep it as it was.
                </p>
                {bothLinked ? (
                    <Notice tone="warning" title="Both are linked to the Catalogue.">
                        Merging archives one, and its links would go with it. Unlink one first (its More menu), then merge it into the other.
                    </Notice>
                ) : null}
                <ErrorNotice error={error} />
                <div className="flex justify-end gap-2">
                    <button type="button" className="btn-secondary" onClick={props.onClose}>
                        Cancel
                    </button>
                    <button type="button" className="btn-primary" disabled={busy || bothLinked} onClick={merge}>
                        {busy ? "Merging..." : "Merge"}
                    </button>
                </div>
            </div>
        </WindowPopup>
    );
}
